/**
 * Model answer → shader. Tolerant by design: models reorder or bold the header,
 * add prose around the code, and streams get cut before the closing fence.
 * Only a missing program is an error.
 */

import type { RuntimeMode } from '../types'
import { parseParams } from '../params'
import { GLSL_INPUT_UNIFORMS, SHADER_INPUT_TYPES, isShaderInputType } from '../media/contract'
import type { ShaderInputType } from '../media/contract'
import { ShaderGenerationError } from './types'
import type { ShaderGenerationResult } from './types'

type HeaderKey = 'title' | 'runtime' | 'inputs' | 'summary'

const HEADER_LINE = /^[ \t>*_#-]*(title|runtime|inputs|summary)[ \t*_]*:[ \t*_]*(.*?)[ \t*_]*$/i
/** A header line that starts the envelope: code after it is the answer, code before it a draft. */
const ANCHOR_LINE = /^[ \t>*_#-]*(title|runtime)[ \t*_]*:/i
/** ``` or ~~~ fences; the closing fence must match the opening one. */
const FENCE = /(^|\n)[ \t]*(```|~~~)[ \t]*([\w.+-]*)[^\n]*\n([\s\S]*?)(?:\n[ \t]*\2|$)/g
const THINKING = /<(think|thinking)>[\s\S]*?(?:<\/\1>|$)/gi
/** A closing tag whose opening one was in the chat template, not the answer. */
const ORPHAN_THINKING_END = /<\/think(?:ing)?>/gi

const GLSL_FENCES = new Set(['glsl', 'frag', 'fs', 'shader', 'hlsl', 'webgl'])
const JS_FENCES = new Set(['js', 'javascript', 'jsx', 'ts', 'typescript', 'mjs'])

const JS_INPUT_PATTERNS: Record<ShaderInputType, RegExp> = {
  image: /\bgenie\.inputImage\b|\bonInput\(\s*['"]image['"]/,
  video: /\bgenie\.inputVideo\b|\bonInput\(\s*['"]video['"]/,
  audio: /\bgenie\.audio\b|\bonInput\(\s*['"]audio['"]/,
  text: /\bgenie\.inputText\b|\bonInput\(\s*['"]text['"]/,
  '3d': /\bgenie\.(inputModel|modelToThree)\b|\bonInput\(\s*['"]3d['"]/,
}

export function parseShaderResponse(text: string): ShaderGenerationResult {
  const clean = stripThinking(text.replace(/\r\n?/g, '\n'))
  const json = readJsonEnvelope(clean)
  if (json) return toResult(json.code, '', json.header)

  const block = findCodeBlock(clean)
  // Header lines are read outside the code, wherever the model put them.
  return toResult(block?.code ?? '', block?.language ?? '', readHeader(block ? clean.replace(FENCE, '\n') : clean))
}

function toResult(rawCode: string, language: string, header: Partial<Record<HeaderKey, string>>): ShaderGenerationResult {
  const code = tidy(rawCode)
  if (!code) throw new ShaderGenerationError('The model answered without any code.', 'EMPTY')
  const runtimeMode = resolveRuntime(header.runtime, language, code)
  const declared = parseInputList(header.inputs ?? '')
  const used = inputsUsedByCode(code, runtimeMode)

  return {
    code,
    runtimeMode,
    inputTypes: SHADER_INPUT_TYPES.filter((type) => declared.includes(type) || used.includes(type)),
    title: unquote(header.title ?? ''),
    summary: unquote(header.summary ?? ''),
    params: parseParams(code),
  }
}

// ---------------------------------------------------------------------------
// Code block
// ---------------------------------------------------------------------------

/**
 * Reasoning is not the answer. Whole `<think>` blocks go; so does everything up
 * to a closing tag with no opening one (templates that put `<think>` in the
 * prompt), which would otherwise leave draft code ahead of the real answer.
 */
function stripThinking(text: string): string {
  const clean = text.replace(THINKING, '')
  let end = -1
  for (const match of clean.matchAll(ORPHAN_THINKING_END)) end = match.index + match[0].length
  return end >= 0 ? clean.slice(end) : clean
}

interface CodeBlock {
  code: string
  language: string
  /** Offset of the opening fence. */
  start: number
  end: number
}

function findCodeBlock(text: string): CodeBlock | null {
  const blocks: CodeBlock[] = []
  for (const match of text.matchAll(FENCE)) {
    const start = match.index + match[1].length
    blocks.push({ code: match[4], language: match[3].toLowerCase(), start, end: match.index + match[0].length })
  }
  // Prefer a block labelled as code, then one after the envelope's header (earlier
  // blocks are drafts), then the longest (a model may add a short usage snippet).
  const labelled = blocks.filter((b) => GLSL_FENCES.has(b.language) || JS_FENCES.has(b.language))
  const pool = afterHeader(text, labelled.length ? labelled : blocks)
  if (pool.length) return pool.reduce((best, b) => (tidy(b.code).length > tidy(best.code).length ? b : best))

  // No fence at all: accept a bare program after the header lines.
  const body = text
    .split('\n')
    .filter((line) => !HEADER_LINE.test(line))
    .join('\n')
  return looksLikeCode(body) ? { code: body, language: '', start: 0, end: text.length } : null
}

/** The blocks after the last TITLE/RUNTIME line that precedes the final block; all of them when there is none. */
function afterHeader(text: string, blocks: CodeBlock[]): CodeBlock[] {
  if (blocks.length < 2) return blocks
  const last = blocks[blocks.length - 1]
  let anchor = -1
  let offset = 0
  for (const line of text.split('\n')) {
    const inside = blocks.some((b) => offset >= b.start && offset < b.end)
    if (offset >= last.start) break
    if (!inside && ANCHOR_LINE.test(line)) anchor = offset
    offset += line.length + 1
  }
  if (anchor < 0) return blocks
  const after = blocks.filter((b) => b.start > anchor)
  return after.length ? after : blocks
}

/**
 * An answer given as JSON instead of the envelope, bare or in a ```json fence:
 * `{ "title", "runtime", "inputs", "summary", "code" | "program" | "source" }`.
 */
function readJsonEnvelope(text: string): { code: string; header: Partial<Record<HeaderKey, string>> } | null {
  const candidates = [text.trim()]
  for (const match of text.matchAll(FENCE)) if (match[3].toLowerCase() === 'json') candidates.push(match[4].trim())
  for (const candidate of candidates) {
    if (!candidate.startsWith('{') || !candidate.endsWith('}')) continue
    let value: unknown
    try {
      value = JSON.parse(candidate)
    } catch {
      continue
    }
    if (typeof value !== 'object' || value === null) continue
    const record = value as Record<string, unknown>
    const code = [record.code, record.program, record.source].find((v): v is string => typeof v === 'string' && v.trim() !== '')
    if (!code) continue
    const field = (key: HeaderKey): string | undefined => {
      const v = record[key]
      if (typeof v === 'string') return v
      if (Array.isArray(v)) return v.filter((item) => typeof item === 'string').join(', ')
      return undefined
    }
    return { code, header: { title: field('title'), runtime: field('runtime'), inputs: field('inputs'), summary: field('summary') } }
  }
  return null
}

function looksLikeCode(text: string): boolean {
  return /\bvoid\s+main\s*\(|gl_FragColor|\bfunction\s+\w+\s*\(|\bcreateCanvas\s*\(|\bTHREE\.|\brequestAnimationFrame\s*\(/.test(text)
}

function tidy(code: string): string {
  return code.replace(/^\s*\n/, '').trimEnd()
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function readHeader(text: string): Partial<Record<HeaderKey, string>> {
  const header: Partial<Record<HeaderKey, string>> = {}
  for (const line of text.split('\n')) {
    const match = HEADER_LINE.exec(line)
    if (!match) continue
    const key = match[1].toLowerCase() as HeaderKey
    if (header[key] === undefined) header[key] = match[2].trim()
  }
  return header
}

function unquote(value: string): string {
  return value.replace(/^["'`]+|["'`]+$/g, '').trim()
}

function parseInputList(value: string): ShaderInputType[] {
  return value
    .toLowerCase()
    .split(/[\s,;|/]+/)
    .map((token) => token.replace(/[^a-z0-9]/g, ''))
    .map((token) => (token === 'model' || token === '3dmodel' ? '3d' : token))
    .filter(isShaderInputType)
}

// ---------------------------------------------------------------------------
// Runtime
// ---------------------------------------------------------------------------

function normalizeRuntime(value: string | undefined): RuntimeMode | null {
  const token = (value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '')
  if (!token) return null
  if (token.startsWith('glsl') || token === 'webgl1' || token === 'fragment') return 'glsl'
  if (token.startsWith('p5')) return 'p5'
  if (token.startsWith('three')) return 'threejs'
  if (token === 'raw' || token === 'canvas' || token === 'canvas2d') return 'raw'
  // "javascript" / "js" names a language, not a runtime: let the code decide.
  return null
}

/** What the code itself looks like, when nothing else says. */
export function detectRuntime(program: string): RuntimeMode {
  const code = stripComments(program)
  const javascript = /\b(function|let|var)\b|=>|\bgenie\.|\bdocument\.|\bwindow\./.test(code)
  if (!javascript && (/\bgl_FragColor\b/.test(code) || /\bvoid\s+main\s*\(/.test(code))) return 'glsl'
  if (/\bTHREE\./.test(code)) return 'threejs'
  if (/\bfunction\s+(setup|draw)\s*\(|\bcreateCanvas\s*\(/.test(code)) return 'p5'
  return 'raw'
}

/** The library a JS program cannot run without: three.js when it touches `THREE`, p5 when it calls `createCanvas`. */
function libraryUsed(program: string): 'threejs' | 'p5' | null {
  const code = stripComments(program)
  if (/\bTHREE\./.test(code)) return 'threejs'
  if (/\bcreateCanvas\s*\(|\bnew\s+p5\s*\(/.test(code)) return 'p5'
  return null
}

/**
 * The header wins, then a GLSL fence label, then the code's own shape. Two
 * model slips are overruled by the code: a header that contradicts it about
 * being GLSL at all, and a JS runtime that lacks the library the code calls
 * (three.js code under "p5" or "raw" would only throw `THREE is not defined`).
 * The header still decides between runtimes the code fits equally well.
 */
function resolveRuntime(header: string | undefined, fenceLanguage: string, code: string): RuntimeMode {
  const detected = detectRuntime(code)
  const declared = normalizeRuntime(header) ?? (GLSL_FENCES.has(fenceLanguage) ? 'glsl' : null)
  if (!declared) return detected
  if ((declared === 'glsl') !== (detected === 'glsl')) return detected
  if (declared === 'glsl') return declared
  const library = libraryUsed(code)
  return library && library !== declared ? library : declared
}

// ---------------------------------------------------------------------------
// Inputs the code reads
// ---------------------------------------------------------------------------

function stripComments(code: string): string {
  return code.replace(/\/\*[\s\S]*?\*\//g, '').replace(/\/\/[^\n]*/g, '')
}

export function inputsUsedByCode(code: string, runtimeMode: RuntimeMode): ShaderInputType[] {
  const source = stripComments(code)
  return SHADER_INPUT_TYPES.filter((type) => {
    if (runtimeMode !== 'glsl') return JS_INPUT_PATTERNS[type].test(source)
    const u = GLSL_INPUT_UNIFORMS[type]
    return [u.sampler, u.has, u.resolution].some((name) => name !== null && new RegExp(`\\b${name}\\b`).test(source))
  })
}
